import axios from 'axios';
import * as mock from './mockApi';
import { enqueueSale, syncQueue, isNetworkError } from './offlineQueue';
import { withSoldBy } from '../utils/soldBy';

export const DEMO_MODE = false;

const API_BASE = process.env.REACT_APP_API_URL ?? '';
const api = axios.create({ baseURL: `${API_BASE}/api`, timeout: 60000 });

// FastAPI request-validation errors (HTTP 422) return `detail` as an ARRAY of
// error objects ({type, loc, msg, input, url}). Pages commonly render
// `err.response.data.detail` directly, so an object/array child crashes React
// (Minified React error #31). Normalise `detail` to a readable string here so
// every caller gets a plain message regardless of the shape FastAPI sent.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err.response?.data?.detail;
    if (Array.isArray(detail)) {
      err.response.data.detail = detail
        .map((e) => (e && typeof e === 'object' ? (e.msg || JSON.stringify(e)) : String(e)))
        .join('; ');
    } else if (detail && typeof detail === 'object') {
      // PH-47 / ADR-010 — some backend errors are a machine-readable {"code": "..."} object with
      // no `msg` (e.g. staff_login_required), not a validation-error shape. Preserve `code`
      // BEFORE flattening `detail` to a string below, so a caller that needs to branch on it
      // (rather than just display it) still can — otherwise it's indistinguishable from any other
      // stringified-JSON error text by the time it reaches a catch block.
      if (detail.code) err.response.data.code = detail.code;
      err.response.data.detail = detail.msg || JSON.stringify(detail);
    }
    return Promise.reject(err);
  },
);

const multipart = (file, path, timeoutMs) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(path, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...(timeoutMs ? { timeout: timeoutMs } : {}),
  });
};

// Stock In — document parsing + AI extraction can take 60–120 s; use 3-minute timeout
export const uploadInvoice = (file) => DEMO_MODE ? mock.mockUploadInvoice(file) : multipart(file, '/stock-in/upload', 180000);
export const confirmStockIn = (data) => DEMO_MODE ? mock.mockConfirmStockIn(data) : api.post('/stock-in/confirm', data);
export const reverseStockIn = (entryName) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.post('/stock-in/reverse', { entry_name: entryName });

// Stock Out
export const lookupBarcode = (barcode) => DEMO_MODE ? mock.mockLookupBarcode(barcode) : api.get(`/stock-out/lookup/${barcode}`);

// PH-53 — refill reminders. Pre-fill days_covered from this patient's last refill of the SAME
// item, when there is one; never guessed from a dose the app does not know.
export const getRefillPrefill = (itemCode, patientPhone) =>
  api.get('/refill-reminders/prefill', { params: { item_code: itemCode, patient_phone: patientPhone } });

// H-21: raw sale POST (always hits the network). Used directly by the offline-queue
// replay so a network failure during replay doesn't re-enqueue and loop.
const rawProcessSale = (data) => api.post('/stock-out/sale', data);

// Resilient sale submit used by the cart. On a genuine network failure (offline /
// no HTTP response) the sale is queued to IndexedDB and a synthetic "queued"
// success is returned so the cashier can move on; it replays automatically on
// reconnect. A SERVER error (any HTTP status — 422 oversell, 500, etc.) is NOT
// queued — it is re-thrown so the cashier sees it. The idempotency_key in the
// payload makes replay safe against a partially-succeeded sale.
export const processSale = async (rawData) => {
  if (DEMO_MODE) return mock.mockProcessSale(rawData);
  const data = withSoldBy(rawData);
  const queuedResponse = () => ({
    data: {
      success: true,
      queued: true,
      invoice_name: null,        // no invoice until synced → print buttons stay hidden
      udhar_balance: null,
      message: 'ऑफलाइन सेव हुआ — ऑनलाइन होने पर अपने आप भेजा जाएगा / Saved offline — will sync automatically when online',
    },
  });
  const queueAndNotify = async () => {
    try {
      await enqueueSale(data);
    } catch (queueErr) {
      // IndexedDB unavailable (iOS Safari private mode, quota exceeded, blocked).
      // We CANNOT hold the sale — surface a clear, actionable error so the cashier
      // writes it down manually rather than seeing an opaque "TransactionInactiveError".
      const e = new Error(
        'ऑफलाइन सेव नहीं हो सका — कृपया बिक्री हाथ से नोट कर लें / ' +
        'Could not save offline — please record this sale manually.'
      );
      e.offlineQueueFailed = true;
      throw e;
    }
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('offline-queue-changed'));
    return queuedResponse();
  };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return queueAndNotify();
  }
  try {
    return await rawProcessSale(data);
  } catch (err) {
    if (isNetworkError(err)) {
      return queueAndNotify();
    }
    throw err;   // real server error — surface to the cashier, do NOT queue
  }
};

// H-21: drain the offline queue (call on reconnect + app load). Returns {synced, failed, remaining}.
export const syncOfflineSales = () => syncQueue(rawProcessSale);

// Dashboard
export const getDashboardSummary = (warehouseId = null) => DEMO_MODE ? mock.mockGetDashboardSummary() : api.get(`/dashboard/summary${warehouseId ? `?warehouse_id=${warehouseId}` : ''}`);
export const getReorderAlerts = (warehouseId = null) => DEMO_MODE ? mock.mockGetReorderAlerts() : api.get(`/dashboard/reorder-alerts${warehouseId ? `?warehouse_id=${warehouseId}` : ''}`);
export const getOutOfStock = (warehouseId = null) => DEMO_MODE ? Promise.resolve({ data: { count: 0, items: [] } }) : api.get(`/dashboard/out-of-stock${warehouseId ? `?warehouse_id=${warehouseId}` : ''}`);
export const getDeadStock = (days = 90, warehouseId = null) => DEMO_MODE ? mock.mockGetDeadStock() : api.get(`/dashboard/dead-stock?days=${days}${warehouseId ? `&warehouse_id=${warehouseId}` : ''}`);
export const getSalesVelocity = (days = 30, warehouseId = null, storesOnly = false) => DEMO_MODE ? mock.mockGetSalesVelocity(days) : api.get(`/dashboard/sales-velocity?days=${days}${warehouseId ? `&warehouse_id=${warehouseId}` : ''}${storesOnly ? '&stores_only=true' : ''}`);
export const getDemandForecast = (itemCode, periods = 4) => api.get(`/dashboard/demand-forecast/${encodeURIComponent(itemCode)}?periods=${periods}`);
export const getPaymentMix = (weeks = 8, warehouseId = null) => api.get(`/dashboard/payment-mix?weeks=${weeks}${warehouseId ? `&warehouse_id=${warehouseId}` : ''}`);
export const getAiSummary = () => api.get('/dashboard/ai-summary');
export const refreshAiSummary = () => api.post('/dashboard/ai-summary/refresh');
export const updateReorderLevel = (item_code, reorder_level) =>
  DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.patch(`/inventory/item/${encodeURIComponent(item_code)}/reorder`, { reorder_level });

// Sales
export const searchSales = (params) => DEMO_MODE ? mock.mockSearchSales(params) : api.get('/sales/search', { params });
export const exportSalesCsv = (fromDate, toDate, saleFilter = 'all') =>
  api.get('/sales/export', { params: { from_date: fromDate, to_date: toDate, sale_filter: saleFilter }, responseType: 'blob' });
export const exportSalesTally = (fromDate, toDate) =>
  api.get('/sales/export/tally', { params: { from_date: fromDate, to_date: toDate }, responseType: 'blob' });
export const getReceiptPdf   = (invoiceName) => api.get(`/sales/receipt/${encodeURIComponent(invoiceName)}`, { responseType: 'blob' });
export const getInvoiceA4Pdf = (invoiceName) => api.get(`/sales/invoice/${encodeURIComponent(invoiceName)}`, { responseType: 'blob' });
export const exportGstr1     = (period) => api.get(`/dashboard/gstr1?period=${encodeURIComponent(period)}`, { responseType: 'blob' });

// Expenses (G-7) + Profit & Loss (G-6)
export const createExpense     = (data) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.post('/expenses', data);
export const getExpenses       = (fromDate, toDate, category) => DEMO_MODE ? Promise.resolve({ data: { expenses: [], total: 0, count: 0 } }) : api.get('/expenses', { params: { from_date: fromDate, to_date: toDate, ...(category ? { category } : {}) } });
export const getExpenseSummary = (fromDate, toDate) => DEMO_MODE ? Promise.resolve({ data: { by_category: [], total: 0 } }) : api.get('/expenses/summary', { params: { from_date: fromDate, to_date: toDate } });
export const deleteExpense     = (id) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.delete(`/expenses/${id}`);
export const getProfitLoss     = (fromDate, toDate) => DEMO_MODE ? Promise.resolve({ data: { net_sales: 0, cogs: 0, gross_profit: 0, gross_margin_pct: 0, expenses_total: 0, expenses_by_category: [], net_profit: 0, net_margin_pct: 0, missing_cost_count: 0, missing_cost_items: [] } }) : api.get('/dashboard/pnl', { params: { from_date: fromDate, to_date: toDate } });

// Customers
export const getCustomerByPhone = (phone) =>
  DEMO_MODE
    ? Promise.resolve({ data: { customer: null, pending_balance: 0 } })
    : api.get(`/customers/by-phone/${encodeURIComponent(phone)}`);

// Udhar
export const getUdhar = (params) => DEMO_MODE ? mock.mockGetUdhar() : api.get('/udhar/', { params });
export const createUdhar = (data) => DEMO_MODE ? mock.mockCreateUdhar(data) : api.post('/udhar/', data);
export const updateUdhar = (id, data) => DEMO_MODE ? mock.mockUpdateUdhar(id, data) : api.patch(`/udhar/${id}`, data);
export const deleteUdhar = (id) => DEMO_MODE ? mock.mockDeleteUdhar(id) : api.delete(`/udhar/${id}`);
export const addUdharPayment = (id, data) => DEMO_MODE ? mock.mockAddUdharPayment(id, data) : api.post(`/udhar/${id}/payment`, data);
export const restoreUdhar = (id) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.post(`/udhar/${id}/restore`);
export const reverseUdharPayment = (entryId, paymentId) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.post(`/udhar/${entryId}/payment/${paymentId}/reverse`);
export const markUdharReminded = (id) =>
  DEMO_MODE ? Promise.resolve({ data: { success: true, last_reminded_at: new Date().toISOString() } })
            : api.post(`/udhar/${id}/remind`);
// Customer-level collection engine (P2): unified Receive Payment + recovery scoreboard.
export const getUdharOutstanding   = (phone) => DEMO_MODE ? Promise.resolve({ data: { total_outstanding: 0, count: 0, entries: [] } }) : api.get('/udhar/outstanding', { params: { phone } });
export const collectUdharPayment   = (data)  => DEMO_MODE ? Promise.resolve({ data: { collected: 0, remaining_outstanding: 0, applied: [], idempotent_replay: false } }) : api.post('/udhar/collect', data);
export const getRecoveryWorklist   = ()      => DEMO_MODE ? Promise.resolve({ data: { count: 0, customers: [] } }) : api.get('/udhar/recovery');
export const getRecoveryScoreboard = (weeks) => DEMO_MODE ? Promise.resolve({ data: {} }) : api.get('/udhar/recovery/scoreboard', { params: weeks ? { weeks } : {} });

// Follow-up
export const getFollowups = (params) => DEMO_MODE ? mock.mockGetFollowups() : api.get('/followup/', { params });
export const createFollowup = (data) => DEMO_MODE ? mock.mockCreateFollowup(data) : api.post('/followup/', data);
export const updateFollowup = (id, data) => DEMO_MODE ? mock.mockUpdateFollowup(id, data) : api.patch(`/followup/${id}`, data);
export const deleteFollowup = (id) => DEMO_MODE ? mock.mockDeleteFollowup(id) : api.delete(`/followup/${id}`);

// Register Photo Sales (new)
export const uploadRegisterPhoto = (file) => DEMO_MODE ? mock.mockUploadRegisterPhoto(file) : multipart(file, '/register/upload');
export const getRegisterJobStatus = (jobId) => DEMO_MODE ? mock.mockGetRegisterJobStatus(jobId) : api.get(`/register/status/${jobId}`);
export const confirmRegisterSale = (data) => DEMO_MODE ? mock.mockConfirmRegisterSale(data) : api.post('/register/confirm', data);
export const saveAlias = (sourceText, itemCode) => DEMO_MODE ? mock.mockSaveAlias(sourceText, itemCode) : api.post('/register/alias', { source_text: sourceText, item_code: itemCode });

// Purchase Orders (no DEMO_MODE mocks — PO feature is admin-only and not used in demo flows)
const _emptyPOList = () => Promise.resolve({ data: { records: [], count: 0, status_counts: {} } });
const _emptyPO     = () => Promise.resolve({ data: { id: 0, po_number: '', supplier_name: '', status: 'Draft', items: [], created_at: new Date().toISOString(), updated_at: new Date().toISOString(), tenant_id: '' } });
export const getPurchaseOrders    = (params) => DEMO_MODE ? _emptyPOList() : api.get('/purchase-orders/', { params });
export const createPurchaseOrder  = (data)   => DEMO_MODE ? _emptyPO()     : api.post('/purchase-orders/', data);
export const getPurchaseOrder     = (id)     => DEMO_MODE ? _emptyPO()     : api.get(`/purchase-orders/${id}`);
export const updatePurchaseOrder  = (id, data) => DEMO_MODE ? _emptyPO()   : api.patch(`/purchase-orders/${id}`, data);
export const editPurchaseOrder    = (id, data) => DEMO_MODE ? _emptyPO()   : api.put(`/purchase-orders/${id}`, data);
export const cancelPurchaseOrder  = (id)     => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.delete(`/purchase-orders/${id}`);
export const receivePurchaseOrder = (id, data) => DEMO_MODE ? _emptyPO()   : api.post(`/purchase-orders/${id}/receive`, data);
export const matchInvoiceToPOs    = (data)     => DEMO_MODE ? Promise.resolve({ data: { matches: [] } }) : api.post('/purchase-orders/match-invoice', data);
export const prefillReceive       = (id, data) => DEMO_MODE ? Promise.resolve({ data: { suggestions: [] } }) : api.post(`/purchase-orders/${id}/prefill-receive`, data);

// Received Credit Notes (PH-42) — reading a credit note received FROM a distributor and telling
// the chemist whether they were short-changed. Two-call upload pattern matches Stock In
// (upload -> draft, nothing persisted; confirm -> persists); reconcile-preview is a third,
// stateless call so the review screen can show the gap breakdown before saving. No DEMO_MODE
// mock layer — this endpoint is new enough that, like getRefillPrefill/getDemandForecast above,
// it goes straight to the API.
export const uploadReceivedCreditNote  = (file) => multipart(file, '/received-credit-notes/upload', 180000);
export const reconcileCreditNotePreview = (data) => api.post('/received-credit-notes/reconcile-preview', data);
export const confirmReceivedCreditNote  = (data) => api.post('/received-credit-notes/confirm', data);
export const listReceivedCreditNotes    = (params) => api.get('/received-credit-notes', { params });
export const getReceivedCreditNote      = (id) => api.get(`/received-credit-notes/${id}`);
export const voidReceivedCreditNote     = (id) => api.post(`/received-credit-notes/${id}/void`);

// Pattern C multi-tenant: the tenant slug comes from the ?t= invite URL and is
// persisted to localStorage by App.jsx. Pre-login requests (login, OTP, config)
// carry it explicitly; once logged in the JWT carries the tenant server-side.
export const getTenantSlug = () => {
  try { return localStorage.getItem('pos_tenant_id') || ''; } catch { return ''; }
};

// Safe extraction of a human error string from an axios error. FastAPI 422 returns `detail` as an
// ARRAY of {msg,...} objects — rendering that array directly as a React child crashes the tree — so
// always funnel error text through this before putting it in state.
export const errText = (err, fallback = 'कुछ गड़बड़ / Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return String(d[0].msg);
  return fallback;
};

// Auth — login (warehouse_id optional: pass null first to get picker, then selected id).
// agent_name is sent only for the collection-agent role (the agent types their own name + per-agent
// PIN — there is no public roster to enumerate); ignored for the fixed roles.
export const loginPin = (role, pin, warehouse_id = null, agent_name = null) =>
  DEMO_MODE
    ? mock.mockLoginPin(role, pin)
    : api.post('/auth/login', { role, pin, warehouse_id, agent_name, tenant_id: getTenantSlug() || undefined }, { withCredentials: true });

// PH-47 / ADR-010 — per-person staff PIN login. staff_id is picked from getLoginOptions()'s
// dropdown, never typed. warehouse_id/mode are resent on the SAME idiom loginPin's own
// warehouse picker already uses: call once with them null/undefined, and if the response comes
// back `needs_mode`/`needs_warehouse` instead of a session, call again with the field filled in.
export const loginStaffPin = (staffId, pin, warehouse_id = null, mode = null) =>
  api.post('/auth/login', { role: 'staff', staff_id: staffId, pin, warehouse_id, mode, tenant_id: getTenantSlug() || undefined }, { withCredentials: true });

// Pre-auth — the per-person login screen's name dropdown (ADR-010 decision 2). Empty/disabled
// while this shop has not cut over to staff-login yet.
export const getLoginOptions = () =>
  api.get('/auth/login-options', getTenantSlug() ? { params: { tenant_id: getTenantSlug() } } : undefined);

// Auth — OTP / PIN reset flow
export const requestOtp = (destination) => DEMO_MODE ? mock.mockRequestOtp(destination) : api.post('/auth/request-otp', { destination, tenant_id: getTenantSlug() || undefined });
// PH-47 — tells verify-otp WHICH channel the code being typed was sent to, so a code a newer
// request superseded is refused clearly (see routers/auth.py verify_otp's own comment).
export const verifyOtpCode = (otp, destination = null) =>
  DEMO_MODE ? mock.mockVerifyOtpCode(otp) : api.post('/auth/verify-otp', { otp, tenant_id: getTenantSlug() || undefined, ...(destination ? { destination } : {}) });
// PH-71 — the old /auth/reset-pins (all three shared role PINs at once) and /auth/staff-setup
// (the forced cutover, proved by the old shared admin PIN + OTP) are both retired along with the
// login path they served. "Forgot PIN" is resetStaffPinApi below, for everyone.

// PH-47 — "Forgot PIN": one OTP token resets ONE named person's PIN, an Admin's included
// (ADR-016 retired the two-code /auth/reset-admin-pin).
export const resetStaffPinApi = (payload) => api.post('/auth/reset-staff-pin', payload);

// Owner Account (PH-46 signup/2FA, PH-58 link, PH-62 the sign-in/link screens) — a GLOBAL identity,
// not tied to this device's configured tenant, so no getTenantSlug() on the login calls. link-shop is
// the one exception: it attaches to THIS device's already-resolved shop, never a typed field.
export const accountLogin = (email, password, tenant_id = null) =>
  api.post('/account/login', { email, password, ...(tenant_id ? { tenant_id } : {}) }, { withCredentials: true });
export const accountLogin2fa = (pending_token, code, tenant_id = null) =>
  api.post('/account/login/2fa', { pending_token, code, ...(tenant_id ? { tenant_id } : {}) }, { withCredentials: true });
// tenant_id has NO default and must be passed in by the page (Settings captures the shop it is
// showing ONCE, on load) — never re-read from the shared localStorage key at request time, since
// a second tab that signed an account into a different shop in between would otherwise make this
// call link to THAT shop while the page still shows this one (post-merge review gate). A default
// of getTenantSlug() would be exactly this footgun waiting for the next caller who forgets to
// pass it (round-2 review finding) — omitting the argument now sends no tenant_id at all, which
// the backend's required-field validation rejects outright instead of silently guessing.
//
// PH-71 — `admin_pin` (the old shared admin PIN) is replaced by `staffProof`: either null (the
// browser already holds an admin-effective staff session for this tenant — the backend checks
// its own cookie, nothing to send) or `{ staff_id, staff_pin, otp_token }` (an Admin person's own
// PIN plus an OTP to the shop's recovery contact).
export const accountLinkShop = (email, password, staffProof, current_code = null, tenant_id) =>
  api.post('/account/link-shop', {
    email, password, tenant_id,
    ...(staffProof ? {
      staff_id: staffProof.staff_id, staff_pin: staffProof.staff_pin, otp_token: staffProof.otp_token,
    } : {}),
    ...(current_code ? { current_code } : {}),
  });
// Owner Account email confirmation (PH-46 shipped the endpoint; PH-69 hung the landing page on it).
// Pre-login and account-global: no tenant slug, no session, and deliberately NO `withCredentials` —
// confirming an address issues nothing to store. Sign-in is a separate, later act.
//
// The token goes in the BODY, never a query string, so it does not reach an access log, a proxy log
// or a Sentry breadcrumb (AGENTS.md §6).
//
// Answers 200 once, then 400 for every refusal, with one of three sentences. Two of them also carry
// a machine-readable `code` in `detail` — which the interceptor above lifts onto
// `err.response.data.code`, and which is what the landing page classifies on rather than the prose:
// code "already_used" (which the screen treats as SUCCESS — for a confirmation link,
// already-verified is a good outcome), code "superseded" (a later signup changed the account's
// password after this link was minted), and a plain, code-less "Invalid or expired verification
// link" — which deliberately
// covers a bad/expired token, a token minted for another purpose, AND an unknown account with ONE
// message, so the response can never be used to discover whether an account exists. Never 429:
// `verify_email` calls neither `check_ip_throttle` nor `record_ip_failure`.
export const accountVerifyEmail = (token) => api.post('/account/verify-email', { token });

// Abandon a session this device could not finish setting up (AccountSignIn): the backend has
// already issued the cookie by then, and leaving it live behind a screen that reports failure is
// worse than one extra request. POST /auth/logout blocklists the token and clears the cookie.
export const logoutSession = () => api.post('/auth/logout', {}, { withCredentials: true });

// Owner Account password reset (PH-46 shipped both endpoints; PH-68 hung the two screens on them).
// Both are pre-login and account-global: no tenant slug, no session, and deliberately NO
// `withCredentials` — neither endpoint issues a cookie, and the confirm below in fact invalidates
// every session the account already had.
//
// Always resolves `{success: true}` for a registered AND an unregistered address — that sameness is
// the security property, so a caller must never infer whether an account exists from this response.
// Errors: 422 for a malformed address (the caller validates first so this should be unreachable),
// and 429 from the per-IP `LoginThrottle` row this endpoint SHARES with the staff PIN login
// (`check_ip_throttle` is keyed on IP alone), so pressing this repeatedly also throttles the
// counter staff's PIN at the till on the same connection.
export const accountPasswordResetRequest = (email) =>
  api.post('/account/password-reset/request', { email });
// 401 for every dead-link reason (bad, expired, superseded epoch, unknown account, already used)
// and 422 when the new password falls outside 8–72 UTF-8 bytes. Never 429: `password_reset_confirm`
// calls neither `check_ip_throttle` nor `record_ip_failure`.
export const accountPasswordResetConfirm = (token, new_password) =>
  api.post('/account/password-reset/confirm', { token, new_password });

// Tenant setup (first-time only)
export const setupTenant = (payload, setupToken) => DEMO_MODE ? mock.mockSetupTenant(payload) : api.post('/tenant/config', payload, { headers: { 'X-Setup-Token': setupToken } });
export const updateTenantConfig = (payload) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.patch('/tenant/config', payload);
export const getTenantConfig = () =>
  DEMO_MODE
    ? Promise.resolve({ data: { shop_name: 'Demo Store', is_configured: true } })
    : api.get('/tenant/config', getTenantSlug() ? { params: { tenant_id: getTenantSlug() } } : undefined);

// WhatsApp Entry (new)
export const parseWhatsApp = (message) => DEMO_MODE ? mock.mockParseWhatsApp(message) : api.post('/register/whatsapp-parse', { message });
export const confirmWhatsAppSale = (data) => DEMO_MODE ? mock.mockConfirmWhatsAppSale(data) : api.post('/register/whatsapp-confirm', data);

// Inventory list — warehouseId overrides JWT warehouse (admin analytics + stock transfer autocomplete)
// aggregate=true is admin-only: returns all warehouses combined for the Analytics all-stores view
export const getInventoryList = (warehouseId = null, aggregate = false) => {
  if (DEMO_MODE) return mock.mockGetInventoryList();
  const params = [];
  if (warehouseId) params.push(`warehouse_id=${warehouseId}`);
  if (aggregate) params.push('aggregate=true');
  return api.get(`/inventory/list${params.length ? `?${params.join('&')}` : ''}`);
};

// Stocktake
export const submitStocktake = (items) => DEMO_MODE ? mock.mockSubmitStocktake(items) : api.post('/inventory/stocktake', { items });
export const submitReconcile = (items) => DEMO_MODE ? mock.mockSubmitReconcile(items) : api.post('/inventory/reconcile', { items });
export const linkBarcode = (itemCode, barcode) => DEMO_MODE ? mock.mockLinkBarcode(itemCode, barcode) : api.post('/inventory/link-barcode', { item_code: itemCode, barcode });
export const createItem = (data) => DEMO_MODE ? Promise.resolve({ data: { success: true, item_code: data.item_code, message: 'Item created (demo)', entry_name: '' } }) : api.post('/inventory/item', data);
export const updateItem = (itemCode, data) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.patch(`/inventory/item/${encodeURIComponent(itemCode)}`, data);

// Compliance reports (D&C Rules 1945): batch expiry + Schedule H1 register.
export const getExpiryReport = (nearDays = 90, warehouseId = null) =>
  DEMO_MODE ? Promise.resolve({ data: { expired: [], near_expiry: [], expired_count: 0, near_expiry_count: 0, expired_value: 0, near_expiry_value: 0 } })
            : api.get('/compliance/expiry-report', { params: { near_days: nearDays, ...(warehouseId ? { warehouse_id: warehouseId } : {}) } });
export const getH1Register = (fromDate, toDate) =>
  DEMO_MODE ? Promise.resolve({ data: { rows: [], count: 0 } })
            : api.get('/compliance/h1-register', { params: { from_date: fromDate, to_date: toDate } });

// Bulk sheet upload (CSV / XLSX)
// mode: "add" | "reconcile" | "items"
export const downloadStockSheetTemplate = (mode) => {
  if (DEMO_MODE) return Promise.resolve({ data: new Blob(['item_code,qty,rate\n'], { type: 'text/csv' }) });
  return api.get(`/inventory/sheet-template?mode=${mode}`, { responseType: 'blob' });
};

export const revertSheetUpload = (snapshotId) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.post(`/inventory/sheet-upload/${snapshotId}/revert`);
export const uploadStockSheet = (file, mode, dryRun = false, overrides = null, unitMappings = null, warehouseId = null) => {
  if (DEMO_MODE) return mock.mockUploadStockSheet(file, mode);
  const fd = new FormData();
  fd.append('file', file);
  if (overrides && Object.keys(overrides).length > 0)
    fd.append('overrides', JSON.stringify(overrides));
  if (unitMappings && Object.keys(unitMappings).length > 0)
    fd.append('unit_mappings', JSON.stringify(unitMappings));
  // Long sheets write row-by-row on confirm (item + opening-stock entry per row),
  // so hundreds of rows exceed the default 60 s — the browser gave up while the
  // server kept processing, which looked like "confirm did nothing". 5-min budget.
  return api.post(`/inventory/sheet-upload?mode=${mode}&dry_run=${dryRun}${warehouseId ? `&warehouse_id=${warehouseId}` : ''}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
};

// Returns
export const searchInvoiceForReturn = (query) => DEMO_MODE ? mock.mockSearchInvoiceForReturn(query) : api.get('/sales/invoice', { params: { query } });
export const processReturn = (data) => DEMO_MODE ? mock.mockProcessReturn(data) : api.post('/sales/return', data);
export const cancelInvoice = (invoiceName) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.patch(`/sales/${encodeURIComponent(invoiceName)}/cancel`);

// Warehouses (admin only)
export const getWarehouses    = ()           => api.get('/tenant/warehouses');
export const createWarehouse  = (data)       => api.post('/tenant/warehouses', data);
export const updateWarehouse  = (id, data)   => api.patch(`/tenant/warehouses/${id}`, data);
export const createStockTransfer = (data)    => api.post('/tenant/stock-transfer', data);

// Customer master
export const listCustomers   = (params)     => api.get('/customers', { params });
export const createCustomer  = (data)       => api.post('/customers', data);
export const updateCustomer  = (id, data)   => api.patch(`/customers/${id}`, data);
export const getCustomer     = (id)         => api.get(`/customers/${id}`);

// Customer 360° — summary (KPIs + purchase history), khata ledger, statement PDF, multi-phone
export const getCustomerSummary   = (id)          => api.get(`/customers/${id}/summary`);
export const getCustomerLedger    = (id)          => api.get(`/customers/${id}/ledger`);
export const getCustomerLedgerPdf = (id)          => api.get(`/customers/${id}/ledger/pdf`, { responseType: 'blob' });
export const addCustomerPhone     = (id, phone)   => api.post(`/customers/${id}/phones`, { phone });
export const deleteCustomerPhone  = (id, phoneId) => api.delete(`/customers/${id}/phones/${phoneId}`);

// Schemes (wholesale module only — admin). Free-goods & slab scheme sheet; router is 403-gated to
// the `wholesale` module server-side, so these only resolve for wholesale/both tenants.
export const listSchemes  = (params)   => api.get('/schemes', { params });
export const getScheme    = (id)       => api.get(`/schemes/${id}`);
export const createScheme = (data)     => api.post('/schemes', data);
export const updateScheme = (id, data) => api.patch(`/schemes/${id}`, data);
export const deleteScheme = (id)       => api.delete(`/schemes/${id}`);
// Scheme accruals — earned free-goods + turnover claims ledger (wholesale)
export const listAccruals = (params)   => api.get('/schemes/accruals', { params });
// Phase 3 — credit-note reconciliation + cushion (settle the claims you earned)
export const getCushion            = (params) => api.get('/schemes/cushion', { params });
export const listCreditNotes       = (params) => api.get('/schemes/credit-notes', { params });
export const createCreditNote      = (data)   => api.post('/schemes/credit-notes', data);
export const applyPendingCreditNotes = ()     => api.post('/schemes/credit-notes/apply-pending');
export const reverseCreditNote     = (id, data) => api.post(`/schemes/credit-notes/${id}/reverse`, data || {});

// Composition / formula search (salt -> in-stock brands ranked by margin + live-scheme).
// Search is available to any role + both business faces (no module gate); curation is admin-only.
export const searchComposition   = (params)  => api.get('/composition/search', { params });
export const listPendingComposition = (params) => api.get('/composition/pending', { params });
export const proposeComposition  = (itemCode) => api.post('/composition/propose', { item_code: itemCode });
export const confirmComposition  = (data)     => api.post('/composition/confirm', data);
export const seedComposition     = (rows)     => api.post('/composition/seed', { rows });
export const backfillComposition = ()         => api.post('/composition/backfill');

// Trade orders — distributor order-taking (wholesale module only; 403-gated server-side).
export const quoteOrder  = (data)     => api.post('/orders/quote', data);   // live QTY+FREE preview, no save
export const createOrder = (data)     => api.post('/orders', data);
export const listOrders  = (params)   => api.get('/orders', { params });
export const getOrder    = (id)       => api.get(`/orders/${id}`);
export const updateOrder = (id, data) => api.patch(`/orders/${id}`, data);

// Cash session / Day close (admin only)
export const openCashSession  = (data)      => api.post('/cash-session/open', data);
export const closeCashSession = (data)      => api.post('/cash-session/close', data);
export const getCashReport    = (date)      => api.get('/cash-session/report', { params: { session_date: date } });
export const listCashSessions = ()          => api.get('/cash-session');
export const createCashOut    = (data)      => api.post('/cash-session/cash-out', data);
export const listCashOuts     = (date)      => api.get('/cash-session/cash-out', { params: { session_date: date } });

// Field collection agent (Pillar 2 — wholesale). The agent CAPTURES (no ledger write); only an admin
// reconcile posts to the ledger (reusing /udhar/collect). See docs/brain/DISTRIBUTOR-ROADMAP.md.
export const getCollectionOutstanding = (phone)  => api.get('/collection/outstanding', { params: { phone } });
export const captureCollection     = (data)      => api.post('/collection/capture', data);
export const getMyCollections      = ()          => api.get('/collection/mine');
export const cancelCollection      = (id)        => api.post(`/collection/${id}/cancel`);
export const getPendingCollections = ()          => api.get('/collection/pending');
export const reconcileCollection   = (id)        => api.post(`/collection/${id}/reconcile`);
export const disputeCollection     = (id, note)  => api.post(`/collection/${id}/dispute`, { note });
export const listCollectionAgents  = ()          => api.get('/collection/agents');
export const createCollectionAgent = (data)      => api.post('/collection/agents', data);
export const updateCollectionAgent = (id, data)  => api.patch(`/collection/agents/${id}`, data);

// Staff roster (PH-15, admin only) — named people picked at login for attribution. Not logins: no PIN.
export const listStaff     = ()                         => api.get('/staff/');
export const createStaff   = (data)                     => api.post('/staff/', data);
export const updateStaff   = (id, data)                 => api.patch(`/staff/${id}`, data);
export const getStaffSales = (fromDate, toDate, warehouseId = null) =>
  api.get('/staff/sales', { params: { from_date: fromDate, to_date: toDate, ...(warehouseId ? { warehouse_id: warehouseId } : {}) } });
// PH-47 / ADR-010 — job/store scope + owner-set PIN, replace-semantics (the caller sends the FULL
// set the person should hold after the call, not a delta).
export const setStaffJobs   = (id, jobs)         => api.put(`/staff/${id}/jobs`, { jobs });
export const setStaffStores = (id, warehouseIds) => api.put(`/staff/${id}/stores`, { warehouse_ids: warehouseIds });
export const setStaffPinApi = (id, pin)          => api.post(`/staff/${id}/pin`, { pin });

// Owner AI chat — "Sahayak" (admin only, read-only). sendChat posts a question; the backend picks a
// read-only tool, runs the tenant-scoped query, and returns a phrased Hinglish reply + follow-up chips.
export const sendChat        = (message, sessionId = null) => api.post('/chat', { message, conversation_id: sessionId });
export const getChatMessages = (conversationId) => api.get(`/chat/messages/${conversationId}`);
export const getChatConfig   = () => api.get('/chat/config');

// Owner control panel — platform operator only (X-Admin-Key protected)
const ownerHeaders = (key) => ({ headers: { 'X-Admin-Key': key } });
export const ownerListTenants  = (key)            => api.get('/owner/tenants', ownerHeaders(key));
// PH-71 — replaces ownerResetPins (all three shared role PINs at once): the operator first sees
// who a shop's people are, then resets ONE named person's own PIN.
export const ownerListTenantStaff = (key, tid)              => api.get(`/owner/tenants/${tid}/staff`, ownerHeaders(key));
export const ownerResetStaffPin   = (key, tid, sid, body)   => api.post(`/owner/tenants/${tid}/staff/${sid}/reset-pin`, body, ownerHeaders(key));
export const ownerResetData    = (key, tid, body) => api.post(`/owner/tenants/${tid}/reset-data`, body, ownerHeaders(key));
export const ownerDeleteTenant = (key, tid, body) => api.delete(`/owner/tenants/${tid}`, { data: body, ...ownerHeaders(key) });
export const ownerCreateInvite       = (key, body)           => api.post('/admin/invite', body, ownerHeaders(key));
export const ownerListInvites        = (key)                 => api.get('/admin/invites', ownerHeaders(key));
export const ownerReissueInvite      = (key, inviteId)        => api.post(`/admin/invite/${inviteId}/reissue`, {}, ownerHeaders(key));
export const ownerRevokeInvite       = (key, inviteId)        => api.delete(`/admin/invite/${inviteId}`, ownerHeaders(key));
export const ownerListWarehouses     = (key, tid)            => api.get(`/owner/tenants/${tid}/warehouses`, ownerHeaders(key));
export const ownerResetWarehouseData = (key, tid, wid, body) => api.post(`/owner/tenants/${tid}/warehouses/${wid}/reset-data`, body, ownerHeaders(key));
export const ownerDeleteWarehouse    = (key, tid, wid, body) => api.delete(`/owner/tenants/${tid}/warehouses/${wid}`, { data: body, ...ownerHeaders(key) });

// Return windows (PH-43, admin only) — how long around expiry (or after invoice) each company still
// credits returned stock, per tenant, plus the shop's own window for its customers. DEMO_MODE
// returns the empty shape so the section renders its calm empty state rather than an error.
const _emptyWindow = () => ({ values: null, source: null, is_known: false, is_guess: false, observation_count: 0, label: '' });
export const getReturnWindows = () =>
  DEMO_MODE ? Promise.resolve({ data: { default: _emptyWindow(), own_customer: _emptyWindow(), suppliers: [] } })
            : api.get('/return-windows');
export const confirmDefaultReturnWindow     = (data) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.put('/return-windows/default', data);
export const confirmSupplierReturnWindow    = (data) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.put('/return-windows/supplier', data);
export const confirmOwnCustomerReturnWindow = (days) => DEMO_MODE ? Promise.resolve({ data: { success: true } }) : api.put('/return-windows/own-customer', { days_after_invoice: days });

export default api;
